import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import DateRangePicker from "../../../ecommerce/globalcomponents/DateRangePicker";
import { subscriptionsData } from "./subscriptionsData";
import {
  formatCurrency,
  formatStuffDate,
  formatStuffLongDate,
  toClassSlug,
  SubscriptionStatus,
} from "./stuffAdapters";
import "./SubscriptionsPage.css";

const renderStatus = (status: SubscriptionStatus) => (
  <span
    className={`subscriptions-status subscriptions-status-${toClassSlug(
      status,
    )}`}
  >
    {status}
  </span>
);

const Subscriptions = () => {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [currentPage, setCurrentPage] = useState(1);

  const filteredSubscriptions = subscriptionsData.filter(
    (subscription) => {
      const term = searchValue.trim().toLowerCase();
      const matchesSearch =
        !term ||
        subscription.productName.toLowerCase().includes(term) ||
        subscription.orderNo.toLowerCase().includes(term);
      const matchesFrom =
        !dateRange.from || subscription.date >= dateRange.from;
      const matchesTo = !dateRange.to || subscription.date <= dateRange.to;
      return matchesSearch && matchesFrom && matchesTo;
    },
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSubscriptions.length / pageSize),
  );
  const page = Math.min(currentPage, totalPages);
  const visibleSubscriptions = filteredSubscriptions.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  return (
    <section className="subscriptions-page">
      <div className="subscriptions-shell">
        <header className="subscriptions-header">
          <div className="subscriptions-heading">
            <span className="subscriptions-heading-icon">
              <Sparkles size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="subscriptions-kicker">MY STUFF</span>
              <h1>Subscriptions</h1>
              <p>
                Track active subscriptions, validity periods, order
                details and product benefits.
              </p>
            </div>
          </div>
          <span className="subscriptions-count-chip">
            {filteredSubscriptions.length} subscriptions
          </span>
        </header>

        <div className="subscriptions-toolbar">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            aria-label="Subscriptions per page"
          >
            <option value={5}>5 Entries</option>
            <option value={10}>10 Entries</option>
            <option value={20}>20 Entries</option>
          </select>

          <div className="subscriptions-date-picker">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <div className="subscriptions-search">
            <Search size={17} strokeWidth={2} />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search subscriptions..."
              aria-label="Search subscriptions"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => setSearchValue("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="subscriptions-desktop-list">
          {visibleSubscriptions.length > 0 ? (
            visibleSubscriptions.map((subscription) => (
              <article
                className="subscriptions-desktop-card"
                key={subscription.id}
                onClick={() => navigate("/SubscriptionDetails")}
                style={{ cursor: "pointer" }}
              >
                <div className="subscriptions-desktop-meta">
                  <div>
                    <span className="subscriptions-meta-label">DATE</span>
                    <strong>
                      {formatStuffDate(subscription.date)}
                    </strong>
                  </div>
                  <div>
                    <span className="subscriptions-meta-label">
                      ORDER NO
                    </span>
                    <strong>{subscription.orderNo}</strong>
                  </div>
                  <div className="subscriptions-desktop-status-block">
                    <span className="subscriptions-meta-label subscriptions-meta-label-status">
                      STATUS
                    </span>
                    {renderStatus(subscription.status)}
                  </div>
                </div>

                <div className="subscriptions-desktop-product">
                  <div className="subscriptions-desktop-product-image">
                    <img
                      src={subscription.productImage}
                      alt={subscription.productName}
                    />
                  </div>
                  <div className="subscriptions-desktop-product-copy">
                    <span>SUBSCRIPTION PRODUCT</span>
                    <h2>{subscription.productName}</h2>
                    <p>{subscription.description}</p>
                    <div className="subscriptions-desktop-price">
                      <span>Price</span>
                      <strong>
                        {formatCurrency(subscription.price)}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className="subscriptions-desktop-footer">
                  <div>
                    <Clock3 size={15} strokeWidth={2} />
                    <span>Validity</span>
                    <strong>{subscription.validity}</strong>
                  </div>
                  <div>
                    <CalendarDays size={15} strokeWidth={2} />
                    <span>Start Date</span>
                    <strong>
                      {formatStuffDate(subscription.startDate)}
                    </strong>
                  </div>
                  <div>
                    <CalendarDays size={15} strokeWidth={2} />
                    <span>End Date</span>
                    <strong>
                      {formatStuffDate(subscription.endDate)}
                    </strong>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="subscriptions-empty">
              <Sparkles size={34} strokeWidth={1.8} />
              <strong>No subscriptions found</strong>
              <span>Try another search or date range.</span>
            </div>
          )}
        </div>

        <div className="subscriptions-mobile-list">
          {visibleSubscriptions.length > 0 ? (
            visibleSubscriptions.map((subscription) => (
              <button
                type="button"
                className="subscriptions-mobile-card"
                key={subscription.id}
                onClick={() => navigate("/SubscriptionDetails")}
              >
                <span className="subscriptions-mobile-serial">
                  {subscription.serialNo}
                </span>
                <div className="subscriptions-mobile-image">
                  <img src={subscription.productImage} alt="" />
                </div>
                <div className="subscriptions-mobile-copy">
                  <div className="subscriptions-mobile-topline">
                    {renderStatus(subscription.status)}
                    <span>
                      <CalendarDays size={13} strokeWidth={2} />
                      {formatStuffLongDate(subscription.date)}
                    </span>
                  </div>
                  <h2>{subscription.productName}</h2>
                  <p>{subscription.description}</p>
                  <div className="subscriptions-mobile-bottomline">
                    <strong>{formatCurrency(subscription.price)}</strong>
                    <span>Order {subscription.orderNo}</span>
                  </div>
                </div>
                <ChevronRight
                  className="subscriptions-mobile-chevron"
                  size={20}
                  strokeWidth={2}
                />
              </button>
            ))
          ) : (
            <div className="subscriptions-mobile-empty">
              <Sparkles size={32} strokeWidth={1.8} />
              <strong>No subscriptions found</strong>
              <span>Try another search or date range.</span>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <footer className="subscriptions-pagination">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setCurrentPage(page - 1)}
              aria-label="Previous page"
            >
              <ChevronRight
                size={18}
                style={{ transform: "rotate(180deg)" }}
              />
            </button>
            <span>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setCurrentPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </footer>
        )}
      </div>
    </section>
  );
};

export default Subscriptions;
